import { Link } from 'react-router-dom'
import { useState } from 'react'

const ARTICLES = [
  {
    category: 'Getting Started',
    items: [
      { q: 'How do I create a household?', a: 'After signing up, you\'ll be guided through the onboarding flow to name your household. You can also create or join a household from Settings → Household.' },
      { q: 'How do I invite family members?', a: 'Go to Members → Invite. Enter the email address of the person you\'d like to invite. They\'ll receive an email with a join link that\'s valid for 7 days.' },
      { q: 'Can I use DoorChart offline?', a: 'Yes! DoorChart is a Progressive Web App and caches your calendar and tasks for offline use. Add it to your home screen for the best experience.' },
      { q: 'How do I add DoorChart to my home screen?', a: 'On iOS: open DoorChart in Safari, tap the Share button, then "Add to Home Screen". On Android: open in Chrome, tap the menu (⋮), then "Add to Home screen" or look for the install prompt.' },
    ],
  },
  {
    category: 'Calendar & Events',
    items: [
      { q: 'How do I create a recurring event?', a: 'When creating or editing an event, tap "Recurrence" and choose daily, weekly, monthly, or custom. You can also set an end date for the series.' },
      { q: 'How do I assign an event to a household member?', a: 'In the event form, use the "Members" field to tag one or more members. Their colour will appear on the calendar dot for that event.' },
      { q: 'Can I edit just one occurrence of a recurring event?', a: 'Yes. Tap the event, tap Edit, and choose "Edit this event only" or "Edit all future events".' },
    ],
  },
  {
    category: 'Tasks & Chores',
    items: [
      { q: 'How do stamp reward cards work?', a: 'Create a stamp card in Rewards with a goal (e.g. 10 stamps). Link it to a task title. Each time that task is completed, a stamp is awarded automatically. When the goal is reached, the reward can be redeemed.' },
      { q: 'Can I set a due date on a task?', a: 'Yes. When creating or editing a task, tap the calendar icon to set a due date. Overdue tasks are highlighted in red.' },
    ],
  },
  {
    category: 'Shopping Lists',
    items: [
      { q: 'How do I share a shopping list?', a: 'Shopping lists are shared with your entire household by default. All members can add, check off, or delete items in real time.' },
      { q: 'Can I add items directly from a recipe?', a: 'Yes. Open a recipe and tap "Add ingredients to shopping list". Items not already in the list will be added automatically.' },
    ],
  },
  {
    category: 'Vault',
    items: [
      { q: 'How secure is the Vault?', a: 'Vault entries are encrypted client-side using AES-256-GCM before being sent to the server. Your password is used (via PBKDF2) to derive the encryption key, which is never transmitted. Even DoorChart cannot read your vault contents.' },
      { q: 'What if I forget my vault password?', a: 'The vault password cannot be recovered — this is by design, since we never store it. If you forget it, you\'ll need to delete all vault entries and create a new vault password.' },
    ],
  },
  {
    category: 'Billing & Plans',
    items: [
      { q: 'What\'s included in the free plan?', a: 'The free plan includes up to 5 household members, calendar, tasks, shopping lists, meal planning, and notes.' },
      { q: 'How does the 7-day trial work?', a: 'When you start a paid subscription, your card won\'t be charged for 7 days. You can cancel within the trial period at no cost.' },
      { q: 'Can I switch between monthly and annual billing?', a: 'Yes. Go to Settings → Billing to change your plan. Switching to annual billing takes effect at your next billing date.' },
    ],
  },
]

const Article = ({ q, a }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-4 text-left gap-3"
      >
        <span className="text-sm font-medium text-text-primary">{q}</span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={`flex-shrink-0 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {open && <p className="pb-4 text-sm text-text-secondary leading-relaxed">{a}</p>}
    </div>
  )
}

export const KnowledgeBase = () => {
  const [search, setSearch] = useState('')
  const q = search.toLowerCase()

  const filtered = ARTICLES
    .map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) => !q || item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
      ),
    }))
    .filter((cat) => cat.items.length > 0)

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 inset-x-0 z-40 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="font-bold text-primary text-lg tracking-tight">DoorChart</Link>
          <Link to="/login" className="text-sm text-text-secondary hover:text-text-primary">Sign in</Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">
        <h1 className="text-3xl font-extrabold text-text-primary mb-2">Help Center</h1>
        <p className="text-sm text-text-muted mb-8">Find answers to common questions about DoorChart.</p>

        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search articles…"
          className="w-full mb-10 px-4 py-3 rounded-xl border border-border bg-surface text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
        />

        {filtered.length === 0 ? (
          <p className="text-center py-12 text-sm text-text-muted">No articles found. Try a different search term or email <a href="mailto:hello@doorchart.com" className="text-primary underline">hello@doorchart.com</a>.</p>
        ) : (
          filtered.map((cat) => (
            <div key={cat.category} className="mb-8">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">{cat.category}</h2>
              <div className="bg-surface rounded-2xl border border-border px-4">
                {cat.items.map((item) => <Article key={item.q} {...item} />)}
              </div>
            </div>
          ))
        )}

        <div className="mt-12 bg-primary-light rounded-2xl p-6 text-center">
          <p className="text-sm font-semibold text-text-primary mb-1">Still need help?</p>
          <p className="text-xs text-text-muted mb-4">Our team typically responds within one business day.</p>
          <a href="mailto:hello@doorchart.com"
            className="inline-block bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-primary/90 transition-colors">
            Email us
          </a>
        </div>
      </div>

      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-3xl mx-auto flex items-center gap-4 text-xs text-text-muted">
          <Link to="/"        className="hover:text-text-primary">Home</Link>
          <Link to="/privacy" className="hover:text-text-primary">Privacy Policy</Link>
          <Link to="/terms"   className="hover:text-text-primary">Terms of Service</Link>
          <a href="mailto:hello@doorchart.com" className="hover:text-text-primary">Contact</a>
        </div>
      </footer>
    </div>
  )
}
